import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
    ApiPaginationResponseDataNonNullable,
    ApiResponse,
    ApiResponseDataNonNullable,
} from "app/shared/models/api-response.model";
import { GenericResponseModel } from "app/shared/models/generic-response.model";
import { Observable } from "rxjs";
import { AuthService } from "../../base-module/base-service/auth.service";
import { UtilitesService } from "../../base-module/base-service/utilites.service";
import { AtualizaStatusBoletaRequestModel } from "../models/lista-boleta/atualiza-status-boleta-request.model";
import { ListaBoletaQueryParams } from "../models/lista-boleta/lista-boleta-query-params.model";
import { MovimentacaoBoletaModel } from "../models/lista-boleta/movimentacao-boleta.model";
import {
    BloqueioBoletaPayload,
    BoletaPayload,
    DeleteAllBoletaPayload,
    UpdateBoletaPayload,
} from "../models/movimentacao-boleta/boleta-payload";
import { BoletaModel, BoletaValorModel } from "../models/movimentacao-boleta/boleta.model";

@Injectable()
export class BoletaService {
    private readonly baseURL = "movimentacao/boleta";
    constructor(
        private authService: AuthService,
        private _http: HttpClient,
        public utilitesService: UtilitesService
    ) {}

    /**
     * Boleta por id
     * @param id ID da Boleta do Cliente
     */
    getById(id: string): Observable<ApiResponseDataNonNullable<BoletaModel>> {
        return this._http.get<ApiResponseDataNonNullable<BoletaModel>>(
            `${this.utilitesService.urlPrefix}${this.baseURL}/${id}`,
            {
                headers: this.authService.getAuthHeaders(),
            }
        );
    }

    /**
     * Boleta por cliente
     * @param id ID da Boleta do Cliente
     */
    getByClienteCount(cliente_id: number, estrategia_id: number): Observable<ApiResponse<{ qtd: number }>> {
        return this._http.get<ApiResponse<{ qtd: number }>>(
            `${this.utilitesService.urlPrefix}${this.baseURL}/cliente/${cliente_id}/${estrategia_id}`,
            {
                headers: this.authService.getAuthHeaders(),
            }
        );
    }

    /**
     * Boleta Abertas por Cliente
     * @param id ID da Boleta do Cliente
     */
    getBoletaAberta(
        cliente_id: number,
        estrategia_id: number
    ): Observable<ApiResponseDataNonNullable<BoletaModel> | ApiResponseDataNonNullable<{ qtd: number }>> {
        return this._http.get<ApiResponseDataNonNullable<BoletaModel> | ApiResponseDataNonNullable<{ qtd: number }>>(
            `${this.utilitesService.urlPrefix}${this.baseURL}/aberta/${cliente_id}/${estrategia_id}`,
            {
                headers: this.authService.getAuthHeaders(),
            }
        );
    }

    /**
     * Lista de Boleta do Cliente
     * @param params Parâmetros de busca
     * @returns Observable com a lista de boletas
     */
    getAll(params: ListaBoletaQueryParams): Observable<ApiPaginationResponseDataNonNullable<MovimentacaoBoletaModel>> {
        const queryParams = this.utilitesService.formurlencoded(params);
        const queryString = queryParams ? "?" + queryParams : "";

        return this._http.get<ApiPaginationResponseDataNonNullable<MovimentacaoBoletaModel>>(
            `${this.utilitesService.urlPrefix}${this.baseURL}${queryString}`,
            { headers: this.authService.getAuthHeaders() }
        );
    }

    /**
     * Delete de Boleta do Cliente
     * @param id ID da Boleta do Cliente
     */
    delete(id: number): Observable<GenericResponseModel> {
        return this._http.delete<GenericResponseModel>(`${this.utilitesService.urlPrefix}${this.baseURL}/${id}`, {
            headers: this.authService.getAuthHeaders(),
        });
    }

    /**
     * Delete All Boleta do Cliente
     * @param params Json Boleta do Cliente
     */
    deleteAll(params: DeleteAllBoletaPayload): Observable<GenericResponseModel> {
        return this._http.post<GenericResponseModel>(
            `${this.utilitesService.urlPrefix}${this.baseURL}/deleteAll`,
            JSON.stringify(params),
            {
                headers: this.authService.getAuthHeaders(),
            }
        );
    }

    /**
     * Cadastra Boleta do Cliente
     * @param payload Json AporBoletate do Cliente
     */
    create(payload: BoletaPayload): Observable<ApiResponseDataNonNullable<BoletaModel>> {
        return this._http.post<ApiResponseDataNonNullable<BoletaModel>>(
            `${this.utilitesService.urlPrefix}${this.baseURL}`,
            JSON.stringify(payload),
            {
                headers: this.authService.getAuthHeaders(),
            }
        );
    }

    /**
     * Atualiza Boleta do Cliente
     * @param payload Json Boleta do Cliente
     */
    update(id: number, payload: UpdateBoletaPayload): Observable<ApiResponseDataNonNullable<BoletaModel>> {
        return this._http.put<ApiResponseDataNonNullable<BoletaModel>>(
            `${this.utilitesService.urlPrefix}${this.baseURL}/${id}`,
            JSON.stringify(payload),
            {
                headers: this.authService.getAuthHeaders(),
            }
        );
    }

    /**
     * Atualiza Status das Boletas
     * @param payload Payload de requisição para atualização de status das boletas
     */
    atualizaStatus(payload: AtualizaStatusBoletaRequestModel): Observable<GenericResponseModel> {
        return this._http.post<GenericResponseModel>(
            `${this.utilitesService.urlPrefix}${this.baseURL}/atualiza-status`,
            JSON.stringify(payload),
            { headers: this.authService.getAuthHeaders() }
        );
    }

    /**
     * Bloqueia Boletas
     * @param params Json Boleta do Cliente
     */
    bloqueio(params: BloqueioBoletaPayload): Observable<GenericResponseModel> {
        return this._http.post<GenericResponseModel>(
            `${this.utilitesService.urlPrefix}${this.baseURL}/bloqueio`,
            JSON.stringify(params),
            {
                headers: this.authService.getAuthHeaders(),
            }
        );
    }

    /**
     * Exporta Lista em Excel
     * @param params Parâmetros de busca
     * @returns Observable com o resultado da exportação
     */
    getAllExport(params: ListaBoletaQueryParams): Observable<ArrayBuffer> {
        const queryParams = this.utilitesService.formurlencoded(params);
        const queryString = queryParams ? "?" + queryParams : "";

        return this._http.get(`${this.utilitesService.urlPrefix}${this.baseURL}/export${queryString}`, {
            responseType: "arraybuffer",
            headers: new HttpHeaders({
                "Content-Type": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,image/apng,*/*;q=0.8",
                Authorization: "Bearer " + this.authService.getToken(),
                "Access-Control-Allow-Origin": "*",
            }),
        });
    }

    /**
     * Boleta por id
     * @param id ID da Boleta do Cliente
     */
    getValoresById(id: number): Observable<ApiPaginationResponseDataNonNullable<BoletaValorModel>> {
        return this._http.get<ApiPaginationResponseDataNonNullable<BoletaValorModel>>(
            `${this.utilitesService.urlPrefix}${this.baseURL}/${id}/valores`,
            {
                headers: this.authService.getAuthHeaders(),
            }
        );
    }
}
